/**
 * API Keys API Service
 *
 * Frontend client for /api/v1/realtors/me/api-keys — the realtor's syndication
 * key management. Mirrors the local ApiResult / buildFailure envelope used by
 * the other realtor services (kept local because the wire DTO uses ISO-string
 * timestamps, distinct from the backend Drizzle row).
 *
 * The full secret (`CreatedApiKey.key`) is returned ONLY from `createApiKey`,
 * exactly once. The list/revoke/update endpoints return masked keys that never
 * carry the secret or its hash.
 */

import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

export interface MaskedApiKey {
	id: string;
	name: string;
	keyPrefix: string;
	allowedDomains: string[];
	lastUsedAt: string | null;
	revokedAt: string | null;
	createdAt: string;
}

export interface CreatedApiKey {
	id: string;
	name: string;
	keyPrefix: string;
	allowedDomains: string[];
	/** The full secret — shown once, never retrievable again. */
	key: string;
	createdAt: string;
}

interface BackendErrorBody {
	message?: string;
	error?: string | null;
}

interface AxiosLikeError {
	response?: { data?: BackendErrorBody; status?: number };
	code?: string;
	message?: string;
}

export interface ApiResult<T> {
	success: boolean;
	error?: string;
	errorCode?: string | null;
	data?: T;
}

function buildFailure<T>(error: unknown, fallback: string): ApiResult<T> {
	if (error == null) {
		return { success: false, error: fallback, errorCode: "UNKNOWN_ERROR" };
	}
	const err = error as AxiosLikeError;
	if (!err.response) {
		return {
			success: false,
			error: "Couldn't reach the server. Check your connection and try again.",
			errorCode: "NETWORK_ERROR",
		};
	}
	return {
		success: false,
		error: err.response.data?.message || fallback,
		errorCode: err.response.data?.error ?? null,
	};
}

export async function listApiKeys(): Promise<ApiResult<MaskedApiKey[]>> {
	try {
		const response = await apiClient.get<{ data: { keys: MaskedApiKey[] } }>(
			`${API_BASE}/realtors/me/api-keys`,
		);
		return { success: true, data: response.data.data.keys };
	} catch (error: unknown) {
		return buildFailure<MaskedApiKey[]>(error, "Failed to load API keys");
	}
}

export async function createApiKey(
	name: string,
	allowedDomains: string[] = [],
): Promise<ApiResult<CreatedApiKey>> {
	try {
		const response = await apiClient.post<{ data: { key: CreatedApiKey } }>(
			`${API_BASE}/realtors/me/api-keys`,
			{ name, allowedDomains },
		);
		return { success: true, data: response.data.data.key };
	} catch (error: unknown) {
		return buildFailure<CreatedApiKey>(error, "Failed to create API key");
	}
}

export async function revokeApiKey(
	id: string,
): Promise<ApiResult<MaskedApiKey>> {
	try {
		const response = await apiClient.delete<{ data: { key: MaskedApiKey } }>(
			`${API_BASE}/realtors/me/api-keys/${id}`,
		);
		return { success: true, data: response.data.data.key };
	} catch (error: unknown) {
		return buildFailure<MaskedApiKey>(error, "Failed to revoke API key");
	}
}

export async function updateApiKeyDomains(
	id: string,
	allowedDomains: string[],
): Promise<ApiResult<MaskedApiKey>> {
	try {
		const response = await apiClient.patch<{ data: { key: MaskedApiKey } }>(
			`${API_BASE}/realtors/me/api-keys/${id}`,
			{ allowedDomains },
		);
		return { success: true, data: response.data.data.key };
	} catch (error: unknown) {
		return buildFailure<MaskedApiKey>(error, "Failed to update domains");
	}
}
